import React from 'react';

import type { AthleteInfo, ColorGroup, Endorsement, ProductInfo, SerializedVariant } from '../types';
import { BackButton } from './BackButton';
import { ColorSelector } from './ColorSelector';
import { CTAButtons } from './CTAButtons';
import { EndorsementsDrawer } from './EndorsementsDrawer';
import { ProductDetailsDrawer } from './ProductDetailsDrawer';
import { ProductHeader } from './ProductHeader';
import { SizeSelector } from './SizeSelector';
import './ProductDetails.css';

interface ProductDetailsProps {
    product: ProductInfo;
    athlete: AthleteInfo;
    colorGroups: ColorGroup[];
    selectedColorGroupIdx: number;
    onSelectColorGroup: (idx: number) => void;
    activeColorGroup: ColorGroup | null;
    selectedVariant: SerializedVariant | null;
    onSelectVariant: (variantId: number) => void;
    endorsements: Endorsement[];
    shopId: number;
}

export function ProductDetails({
    product,
    athlete,
    colorGroups,
    selectedColorGroupIdx,
    onSelectColorGroup,
    activeColorGroup,
    selectedVariant,
    onSelectVariant,
    endorsements,
    shopId,
}: ProductDetailsProps) {
    const hasColorOptions = colorGroups.length > 1;
    const hasSizeOptions = (activeColorGroup?.variants ?? []).some((v) => v.size !== null && v.size !== '');
    const hasVariantOptions = hasColorOptions || hasSizeOptions;

    return (
        <section className="pdp-details">
            <div className="pdp-details__scroll">
                <BackButton athleteSlug={athlete.slug} athleteFirstName={athlete.first_name} />
                <ProductHeader
                    brand={product.brand}
                    name={product.name}
                    price={selectedVariant?.price ?? null}
                />
                {hasVariantOptions && (
                    <>
                        <hr className="pdp-details__divider" />
                        <ColorSelector
                            colorGroups={colorGroups}
                            selectedColorGroupIdx={selectedColorGroupIdx}
                            onSelect={onSelectColorGroup}
                        />
                        <SizeSelector
                            variants={activeColorGroup?.variants ?? []}
                            selectedVariantId={selectedVariant?.id ?? null}
                            onSelect={onSelectVariant}
                        />
                    </>
                )}
                {endorsements.length > 0 && (
                    <>
                        <hr className="pdp-details__divider" />
                        <EndorsementsDrawer endorsements={endorsements} athleteImage={athlete.profile_image} />
                    </>
                )}
                {product.description && (
                    <>
                        <hr className="pdp-details__divider" />
                        <ProductDetailsDrawer description={product.description} />
                    </>
                )}
            </div>
            <CTAButtons product={product} variant={selectedVariant} shopId={shopId} />
        </section>
    );
}
